import React, { useEffect, useState, memo } from 'react';
import { GlassesIcon, SmartphoneIcon, MonitorIcon, CloudIcon, BuildingIcon, ChevronRightIcon, PlayCircleIcon, CheckCircleIcon, ArrowRightIcon, ZapIcon, ShieldIcon, UsersIcon, TrendingUpIcon, MessageSquareIcon, SettingsIcon, FileIcon, VideoIcon, MicIcon, BrainIcon, LockIcon, BarChart3Icon, CodeIcon, PackageIcon, GitBranchIcon, TerminalIcon, BookOpenIcon, MenuIcon, XIcon, PlugIcon, SmartphoneIcon as AndroidIcon, LayersIcon, SparklesIcon } from 'lucide-react';
export function AriOSMarketing() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('home');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    deviceType: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  // Set page title
  useEffect(() => {
    document.title = 'AR Operating System';
  }, []);
  // Track mouse position for parallax effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  // Helper function to get border color based on section
  const getSectionColor = (section: string) => {
    const colorMap: {
      [key: string]: string;
    } = {
      home: '#E9A165',
      'ari-os': '#E9A165',
      chat: '#D25796',
      cloud: '#8846DE',
      business: '#D25796',
      'mobile-app': '#8846DE',
      architecture: '#E9A165',
      developers: '#8846DE'
    };
    return colorMap[section] || '#E9A165';
  };
  const getActiveButtonClass = (section: string) => {
    return activeSection === section ? `border-2 text-white` : 'text-gray-300 hover:text-white hover:bg-white/5 border-2 border-transparent';
  };
  const getActiveBorderStyle = (section: string) => {
    if (activeSection === section) {
      return {
        borderColor: getSectionColor(section)
      };
    }
    return {};
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      // Determine which section is currently in view
      const sections = ['home', 'ari-os', 'chat', 'cloud', 'business', 'mobile-app', 'architecture', 'developers'];
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Check if section is in the middle of viewport
          if (rect.top <= 300 && rect.bottom >= 300) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 40;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setFormSubmitted(true);
    setTimeout(() => {
      setContactModalOpen(false);
      setFormSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        deviceType: '',
        message: ''
      });
    }, 2000);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <div className="w-full min-h-screen bg-black relative overflow-hidden" style={{
    zoom: '0.9'
  }} data-id="element-35">
      {/* Enhanced Animated Background with Apple-style depth */}
      <div className="fixed inset-0 z-0" data-id="element-36">
        {/* Base gradient with smooth transitions */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-zinc-900 transition-all duration-1000" data-id="element-37"></div>

        {/* Animated gradient orbs with parallax */}
        <div className="absolute inset-0 opacity-30 transition-all duration-700 ease-out" style={{
        transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`
      }} data-id="element-38">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-radial from-[#E9A165]/20 via-[#E9A165]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" data-id="element-39"></div>
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-gradient-radial from-[#8846DE]/20 via-[#8846DE]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" style={{
          animationDelay: '1s'
        }} data-id="element-40"></div>
          <div className="absolute bottom-1/4 left-1/3 w-[550px] h-[550px] bg-gradient-radial from-[#D25796]/20 via-[#D25796]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" style={{
          animationDelay: '2s'
        }} data-id="element-41"></div>
        </div>

        {/* Glossy mesh gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(233,161,101,0.12),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(136,70,222,0.12),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(210,87,150,0.12),transparent_50%)] opacity-60" data-id="element-42"></div>

        {/* Subtle animated grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px] opacity-20 animate-grid-flow" data-id="element-43"></div>

        {/* Glass reflection effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-transparent" data-id="element-44"></div>
      </div>

      {/* Sticky Header Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800 shadow-2xl' : 'bg-transparent'}`} data-id="element-45">
        <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-46">
          <div className="flex items-center justify-between" data-id="element-47">
            {/* Logo */}
            <button onClick={() => scrollToSection('home')} className="flex items-center space-x-3 group" data-id="element-48">
              <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-8 group-hover:scale-110 transition-transform" data-id="element-49" />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2" data-id="element-50">
              <button onClick={() => scrollToSection('home')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('home')}`} style={getActiveBorderStyle('home')} data-id="element-51">
                Home
              </button>
              <button onClick={() => scrollToSection('ari-os')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('ari-os')}`} style={getActiveBorderStyle('ari-os')} data-id="element-52">
                Ari OS
              </button>
              <button onClick={() => scrollToSection('chat')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('chat')}`} style={getActiveBorderStyle('chat')} data-id="element-53">
                Chat
              </button>
              <button onClick={() => scrollToSection('cloud')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('cloud')}`} style={getActiveBorderStyle('cloud')} data-id="element-54">
                Cloud
              </button>
              <button onClick={() => scrollToSection('business')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('business')}`} style={getActiveBorderStyle('business')} data-id="element-55">
                Business
              </button>
              <button onClick={() => scrollToSection('mobile-app')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('mobile-app')}`} style={getActiveBorderStyle('mobile-app')} data-id="element-56">
                Mobile App
              </button>
              <button onClick={() => scrollToSection('architecture')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('architecture')}`} style={getActiveBorderStyle('architecture')} data-id="element-57">
                Architecture
              </button>
              <button onClick={() => scrollToSection('developers')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('developers')}`} style={getActiveBorderStyle('developers')} data-id="element-58">
                Developers
              </button>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3" data-id="element-59">
              <a href="https://app.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-lg hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all text-sm font-semibold" data-id="element-60">
                Try Ari
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-61">
              {mobileMenuOpen ? <XIcon size={24} data-id="element-62" /> : <MenuIcon size={24} data-id="element-63" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-2 bg-zinc-950/95 backdrop-blur-xl rounded-b-2xl" data-id="element-64">
              <button onClick={() => scrollToSection('home')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('home')}`} style={getActiveBorderStyle('home')} data-id="element-65">
                Home
              </button>
              <button onClick={() => scrollToSection('ari-os')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('ari-os')}`} style={getActiveBorderStyle('ari-os')} data-id="element-66">
                Ari OS
              </button>
              <button onClick={() => scrollToSection('chat')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('chat')}`} style={getActiveBorderStyle('chat')} data-id="element-67">
                Chat
              </button>
              <button onClick={() => scrollToSection('cloud')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('cloud')}`} style={getActiveBorderStyle('cloud')} data-id="element-68">
                Cloud
              </button>
              <button onClick={() => scrollToSection('business')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('business')}`} style={getActiveBorderStyle('business')} data-id="element-69">
                Business
              </button>
              <button onClick={() => scrollToSection('mobile-app')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('mobile-app')}`} style={getActiveBorderStyle('mobile-app')} data-id="element-70">
                Mobile App
              </button>
              <button onClick={() => scrollToSection('architecture')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('architecture')}`} style={getActiveBorderStyle('architecture')} data-id="element-71">
                Architecture
              </button>
              <button onClick={() => scrollToSection('developers')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('developers')}`} style={getActiveBorderStyle('developers')} data-id="element-72">
                Developers
              </button>
              <div className="pt-4 space-y-2" data-id="element-73">
                <a href="https://app.ari-os.com" target="_blank" rel="noopener noreferrer" className="w-full px-4 py-3 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-lg hover:shadow-xl transition-all text-sm font-semibold block text-center" data-id="element-74">
                  Try Ari
                </a>
              </div>
            </div>}
        </div>
      </header>

      {/* Main Content - All sections in one flow */}
      <div className="relative z-10" data-id="element-75">
        {/* Hero Section */}
        <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-6" data-id="element-76">
          <div className="max-w-7xl mx-auto text-center" data-id="element-77">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mt-12 mb-8 leading-tight" data-id="element-78">
              Voice-First OS for the
              <br data-id="element-79" />
              <span className="bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent animate-gradient" data-id="element-80">
                Next Generation of Devices
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-4xl mx-auto leading-relaxed" data-id="element-81">
              Work hands-free across all your devices with AI-powered voice
              control and intelligent cloud sync
            </p>

            {/* CTA Buttons - Updated to scroll instead of navigate */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-82">
              <a href="https://app.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-10 py-5 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg group relative overflow-hidden" data-id="element-83">
                <span className="relative z-10" data-id="element-84">Try Ari Now</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#D25796] via-[#8846DE] to-[#E9A165] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-85"></div>
              </a>
              <button onClick={() => scrollToSection('architecture')} className="px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-86">
                Ecosystem Architecture
              </button>
            </div>

            {/* Video Placeholder */}
            <div className="relative max-w-6xl mx-auto" data-id="element-87">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-88"></div>
            </div>
          </div>
        </section>

        {/* Ari OS Section */}
        <section id="ari-os" className="min-h-screen flex items-center py-32 px-6" data-id="element-89">
          <div className="max-w-7xl mx-auto" data-id="element-90">
            <div className="text-center mb-16" data-id="element-91">
              <div className="inline-block mb-6" data-id="element-92">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 to-[#D25796]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-93">
                  <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-4 inline-block" data-id="element-94" />
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-95">
                Ari on Device
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-96">
                Control your entire device with just your voice, hands-free
                productivity at its finest
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-97">
              <div data-id="element-98">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-99">
                  Voice-First Interface
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-100">
                  Control everything with natural voice commands. Ari
                  understands context and responds intelligently to help you
                  stay productive.
                </p>
                <div className="space-y-6" data-id="element-101">
                  <div className="flex items-start space-x-4 group" data-id="element-102">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-103">
                      <MicIcon size={24} className="text-white" data-id="element-104" />
                    </div>
                    <div data-id="element-105">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-106">
                        Natural Language Processing
                      </h4>
                      <p className="text-zinc-500" data-id="element-107">
                        Speak naturally, Ari understands intent and context
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-108">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-109">
                      <BrainIcon size={24} className="text-white" data-id="element-110" />
                    </div>
                    <div data-id="element-111">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-112">
                        Contextual Awareness
                      </h4>
                      <p className="text-zinc-500" data-id="element-113">
                        Remembers previous conversations and preferences
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-114">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-115">
                      <ZapIcon size={24} className="text-white" data-id="element-116" />
                    </div>
                    <div data-id="element-117">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-118">
                        Instant Response
                      </h4>
                      <p className="text-zinc-500" data-id="element-119">
                        Lightning-fast processing for real-time assistance
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-120">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-121"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-122">
                  <img src="https://cdn.magicpatterns.com/uploads/7sJmhbQwXp2rt8FjRa293u/devices_new_3.png" alt="Ari OS Devices Ecosystem" className="w-full h-full object-cover" data-id="element-123" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-124">
              <div className="order-2 lg:order-1 relative" data-id="element-125">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-126"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-127">
                  <img src="https://cdn.magicpatterns.com/uploads/nT6uLZCb3k3Aij25WepRHP/person_talking_2.png" alt="Hands-Free Control Demo - Person using AR glasses with voice commands" className="w-full h-full object-cover" data-id="element-128" />
                </div>
              </div>
              <div className="order-1 lg:order-2" data-id="element-129">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-130">
                  Complete Voice Control
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-131">
                  Take pictures, make calls, reply to emails, send messages, and
                  control your entire device - all hands-free with just your
                  voice.
                </p>
                <div className="space-y-6" data-id="element-132">
                  <div className="flex items-start space-x-4 group" data-id="element-133">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-134">
                      <GlassesIcon size={24} className="text-white" data-id="element-135" />
                    </div>
                    <div data-id="element-136">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-137">
                        Hands-Free Photography
                      </h4>
                      <p className="text-zinc-500" data-id="element-138">
                        Take pictures and videos without touching your device
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-139">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-140">
                      <VideoIcon size={24} className="text-white" data-id="element-141" />
                    </div>
                    <div data-id="element-142">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-143">
                        Microsoft Teams & Calls
                      </h4>
                      <p className="text-zinc-500" data-id="element-144">
                        Make calls on Teams and other platforms with voice
                        commands
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-145">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-146">
                      <MessageSquareIcon size={24} className="text-white" data-id="element-147" />
                    </div>
                    <div data-id="element-148">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-149">
                        Email & Messaging
                      </h4>
                      <p className="text-zinc-500" data-id="element-150">
                        Reply to emails and send messages using only your voice
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-151">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-152">
                      <MicIcon size={24} className="text-white" data-id="element-153" />
                    </div>
                    <div data-id="element-154">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-155">
                        Talk to Ari
                      </h4>
                      <p className="text-zinc-500" data-id="element-156">
                        Ask questions and get instant answers from your AI
                        assistant
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Web Portal Section */}
        <section id="chat" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-zinc-950" data-id="element-157">
          <div className="max-w-7xl mx-auto" data-id="element-158">
            <div className="text-center mb-16" data-id="element-159">
              <div className="inline-block mb-6" data-id="element-160">
                <div className="px-6 py-2 bg-gradient-to-r from-[#D25796]/20 to-[#E9A165]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-161">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#D25796] to-[#E9A165] bg-clip-text text-transparent" data-id="element-162">
                    CHAT
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-163">
                Conversational Interface
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-164">
                Talk to Ari from anywhere - your computer, headset, or mobile
                device
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16" data-id="element-165">
              <div className="relative" data-id="element-166">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D25796] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-167"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-168">
                  <img src="https://cdn.magicpatterns.com/uploads/6F5gyPGn14eCsFN6uaP8Ti/image.png" alt="Ari Chat Interface - Voice-Powered Assistant" className="w-full h-full object-cover" data-id="element-169" />
                </div>
              </div>
              <div data-id="element-170">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-171">
                  Voice-Powered Assistant
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-172">
                  Use your voice to talk to Ari and get help with emails,
                  messages, calendar, and any questions you have. Chat from your
                  computer, headset, or mobile device.
                </p>
                <div className="space-y-4" data-id="element-173">
                  <div className="flex items-start space-x-3" data-id="element-174">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-175">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-176" />
                    </div>
                    <p className="text-zinc-400" data-id="element-177">
                      Help with mail, messages, and calendar
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-178">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-179">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-180" />
                    </div>
                    <p className="text-zinc-400" data-id="element-181">
                      Answer any question instantly
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-182">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-183">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-184" />
                    </div>
                    <p className="text-zinc-400" data-id="element-185">
                      Chat from computer, headset, or mobile
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-186">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-187">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-188" />
                    </div>
                    <p className="text-zinc-400" data-id="element-189">
                      Supports all major languages
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-190">
              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-191">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-192">
                  <MessageSquareIcon size={24} className="text-white" data-id="element-193" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-194">
                  Conversation History
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-195">
                  View and search through all your past conversations with Ari
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-196">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-197">
                  <GlassesIcon size={24} className="text-white" data-id="element-198" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-199">
                  Switch Between Devices
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-200">
                  Seamlessly switch chats between headset and web interface
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-201">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-202">
                  <ZapIcon size={24} className="text-white" data-id="element-203" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-204">
                  All Ari Skills
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-205">
                  Access all of Ari's capabilities from the chat interface
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-206">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-207">
                  <UsersIcon size={24} className="text-white" data-id="element-208" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-209">
                  Manage Profile
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-210">
                  Update your profile and subscription status anytime
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-211">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-212">
                  <SettingsIcon size={24} className="text-white" data-id="element-213" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-214">
                  Activate Skills
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-215">
                  Enable or disable Ari skills based on your needs
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-216">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-217">
                  <PlugIcon size={24} className="text-white" data-id="element-218" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-219">
                  Manage Integrations
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-220">
                  Add or remove integrations with third-party services
                </p>
              </div>
            </div>

            <div className="mt-12 text-center" data-id="element-221">
              <a href="https://app.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#D25796] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#D25796]/50 transition-all font-bold text-lg" data-id="element-222">
                Start Chatting
              </a>
            </div>
          </div>
        </section>

        {/* Cloud Section */}
        <section id="cloud" className="min-h-screen flex items-center py-32 px-6" data-id="element-223">
          <div className="max-w-7xl mx-auto" data-id="element-224">
            <div className="text-center mb-16" data-id="element-225">
              <div className="inline-block mb-6" data-id="element-226">
                <div className="px-6 py-2 bg-gradient-to-r from-[#8846DE]/20 to-[#E9A165]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-227">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#8846DE] to-[#E9A165] bg-clip-text text-transparent" data-id="element-228">
                    USER CLOUD
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-229">
                Your Personal Hub
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-230">
                AI-powered cloud storage that remembers everything and keeps you
                in control
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16" data-id="element-231">
              <div data-id="element-232">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-233">
                  Intelligent Storage
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-234">
                  Your personal AI hub that stores files, remembers context, and
                  keeps everything in sync across all your devices.
                </p>

                {/* AI Memory */}
                <div className="mb-8" data-id="element-235">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-236">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-237">
                      <BrainIcon size={20} className="text-white" data-id="element-238" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-239">AI Memory</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-240">
                    <div className="flex items-start space-x-3" data-id="element-241">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-242">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-243" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-244">
                        Remembers your preferences and habits
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-245">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-246">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-247" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-248">
                        Recalls previous conversations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-249">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-250">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-251" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-252">
                        View and edit what AI stores about you
                      </p>
                    </div>
                  </div>
                </div>

                {/* Files & Knowledge */}
                <div className="mb-8" data-id="element-253">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-254">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-255">
                      <FileIcon size={20} className="text-white" data-id="element-256" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-257">
                      Files & Knowledge
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-258">
                    <div className="flex items-start space-x-3" data-id="element-259">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-260">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-261" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-262">
                        Upload documents and notes
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-263">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-264">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-265" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-266">
                        Ask AI questions about your files
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-267">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-268">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-269" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-270">
                        Import from Google Drive, OneDrive, Dropbox
                      </p>
                    </div>
                  </div>
                </div>

                {/* Devices */}
                <div data-id="element-271">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-272">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-273">
                      <SmartphoneIcon size={20} className="text-white" data-id="element-274" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-275">
                      Device Management
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-276">
                    <div className="flex items-start space-x-3" data-id="element-277">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-278">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-279" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-280">
                        See all connected devices
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-281">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-282">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-283" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-284">
                        Control sync frequency
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-285">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-286">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-287" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-288">
                        Remotely disconnect devices
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div data-id="element-289">
                {/* Media */}
                <div className="mb-8" data-id="element-290">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-291">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-292">
                      <VideoIcon size={20} className="text-white" data-id="element-293" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-294">Media</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-295">
                    <div className="flex items-start space-x-3" data-id="element-296">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-297">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-298" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-299">
                        Auto-saves photos/videos from devices
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-300">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-301">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-302" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-303">
                        View media timeline anytime
                      </p>
                    </div>
                  </div>
                </div>

                {/* Skills & Integrations */}
                <div className="mb-8" data-id="element-304">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-305">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-306">
                      <SettingsIcon size={20} className="text-white" data-id="element-307" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-308">
                      Skills & Integrations
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-309">
                    <div className="flex items-start space-x-3" data-id="element-310">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-311">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-312" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-313">
                        Add or remove Ari skills
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-314">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-315">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-316" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-317">
                        Manage third-party integrations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-318">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-319">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-320" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-321">
                        Customize skill settings
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-322">
                  <div className="absolute -inset-4 bg-gradient-to-r from-[#8846DE] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-323"></div>
                  <div className="relative bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 overflow-hidden" data-id="element-324">
                    <img src="https://cdn.magicpatterns.com/uploads/nPnXM4vnWRjVHqvZQRMm5A/image.png" alt="Ari Cloud Dashboard - Account, Memory, Chat History, Media Collection" className="w-full h-auto max-h-[600px] object-cover object-top" data-id="element-325" />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-326">
              <a href="https://cloud.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#8846DE] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg" data-id="element-327">
                Explore User Cloud
              </a>
            </div>
          </div>
        </section>

        {/* Business Section */}
        <section id="business" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-id="element-328">
          <div className="max-w-7xl mx-auto" data-id="element-329">
            <div className="text-center mb-16" data-id="element-330">
              <div className="inline-block mb-6" data-id="element-331">
                <div className="px-6 py-2 bg-gradient-to-r from-[#D25796]/20 to-[#E9A165]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-332">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#D25796] to-[#E9A165] bg-clip-text text-transparent" data-id="element-333">
                    BUSINESS CLOUD
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-334">
                Enterprise Control
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-335">
                Fleet management and analytics for organizations
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-336">
              <div className="relative" data-id="element-337">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D25796] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-338"></div>
                <div className="relative aspect-video bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl border-2 border-white/10 flex items-center justify-center overflow-hidden" data-id="element-339">
                  <img src="https://cdn.magicpatterns.com/uploads/iRVkz4jdtoc25hx5UZzgBd/business_cloud.png" alt="Ari Business Cloud - Enterprise Dashboard with Device Status, Firmware, and Reporting" className="w-full h-full object-cover" data-id="element-340" />
                </div>
              </div>
              <div data-id="element-341">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-342">
                  Enterprise-Grade Management
                </h3>
                <p className="text-gray-300 mb-10 text-lg leading-relaxed" data-id="element-343">
                  Manage fleets of AR devices, deploy updates, monitor usage,
                  and maintain security policies at scale.
                </p>
                <div className="space-y-6 mb-10" data-id="element-344">
                  <div className="flex items-start space-x-4 group" data-id="element-345">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-346">
                      <UsersIcon size={24} className="text-white" data-id="element-347" />
                    </div>
                    <div data-id="element-348">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-349">
                        Fleet Management
                      </h4>
                      <p className="text-gray-400" data-id="element-350">
                        Centralized control of all devices
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-351">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-352">
                      <LockIcon size={24} className="text-white" data-id="element-353" />
                    </div>
                    <div data-id="element-354">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-355">
                        Security Policies
                      </h4>
                      <p className="text-gray-400" data-id="element-356">
                        Enterprise-grade security controls
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-357">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-358">
                      <TrendingUpIcon size={24} className="text-white" data-id="element-359" />
                    </div>
                    <div data-id="element-360">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-361">
                        Analytics & Insights
                      </h4>
                      <p className="text-gray-400" data-id="element-362">
                        Usage metrics and performance data
                      </p>
                    </div>
                  </div>
                </div>
                <a href="https://business.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#D25796] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#D25796]/50 transition-all font-bold text-lg" data-id="element-363">
                  Enterprise Solutions
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile App Section */}
        <section id="mobile-app" className="min-h-screen flex items-center py-32 px-6" data-id="element-364">
          <div className="max-w-7xl mx-auto" data-id="element-365">
            <div className="text-center mb-16" data-id="element-366">
              <div className="inline-block mb-6" data-id="element-367">
                <div className="px-6 py-2 bg-gradient-to-r from-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-368">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-369">
                    MOBILE APP
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-370">
                Ari Companion App
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-371">
                Your AR device companion for iOS and Android - setup, control,
                and manage everything
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16" data-id="element-372">
              <div data-id="element-373">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-374">
                  Easy Setup & Control
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-375">
                  Get started in seconds with automatic device discovery and
                  one-tap pairing. Manage all your AR devices from one place.
                </p>

                {/* Easy Setup */}
                <div className="mb-8" data-id="element-376">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-377">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-378">
                      <ZapIcon size={20} className="text-white" data-id="element-379" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-380">Easy Setup</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-381">
                    <div className="flex items-start space-x-3" data-id="element-382">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-383">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-384" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-385">
                        Automatically finds AR devices nearby
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-386">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-387">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-388" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-389">
                        One-tap pairing with your device
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-390">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-391">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-392" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-393">
                        Connect to Bluetooth headphones
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-394">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-395">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-396" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-397">
                        Quickly send Wi-Fi login details
                      </p>
                    </div>
                  </div>
                </div>

                {/* Device Controls */}
                <div className="mb-8" data-id="element-398">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-399">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-400">
                      <SettingsIcon size={20} className="text-white" data-id="element-401" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-402">
                      Device Controls
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-403">
                    <div className="flex items-start space-x-3" data-id="element-404">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-405">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-406" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-407">
                        Manage multiple headsets from one phone
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-408">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-409">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-410" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-411">
                        Adjust volume, brightness, and settings
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-412">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-413">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-414" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-415">
                        Manage profile and subscription status
                      </p>
                    </div>
                  </div>
                </div>

                {/* Media & Chat */}
                <div data-id="element-416">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-417">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-418">
                      <MessageSquareIcon size={20} className="text-white" data-id="element-419" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-420">
                      Media & Chat
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-421">
                    <div className="flex items-start space-x-3" data-id="element-422">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-423">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-424" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-425">
                        View and delete photos/videos from device
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-426">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-427">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-428" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-429">
                        Talk to Ari on your phone
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-430">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-431">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-432" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-433">
                        View Ari conversation history
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-434">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-435">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-436" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-437">
                        Add and remove Ari integrations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-438">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-439">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-440" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-441">
                        Customize profile and themes
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative flex items-center justify-center" data-id="element-442">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#8846DE] to-[#D25796] rounded-[3rem] opacity-20 blur-3xl" data-id="element-443"></div>

                {/* Phone Mockup */}
                <div className="relative w-[340px] h-[700px]" data-id="element-444">
                  {/* Phone Frame */}
                  <div className="absolute inset-0 bg-gradient-to-b from-zinc-700 to-zinc-800 rounded-[3rem] shadow-2xl border-[3px] border-zinc-600/50" data-id="element-445">
                    {/* Inner bezel */}
                    <div className="absolute inset-[10px] bg-zinc-900 rounded-[2.4rem]" data-id="element-446">
                      {/* Screen */}
                      <div className="absolute inset-[3px] rounded-[2.2rem] overflow-hidden" data-id="element-447">
                        <img src="https://cdn.magicpatterns.com/uploads/x2mGyh8RJbMReKTSfHvhJC/Screen_Andorid.jpg" alt="Ari Companion App - Device management screen showing Arc3-3813 connected with battery, display, volume controls and quick actions" className="w-full h-full object-cover" data-id="element-448" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-449">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-450">
                <a href="https://play.google.com/store/apps/details?id=com.ari_os.companion&hl=en" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-3 px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-451">
                  <img src="https://cdn.magicpatterns.com/uploads/grMQzHgPVptgUWdveugAA9/163-1632345_download-the-domus-app-today-android-icon-white.png" alt="Android" className="w-6 h-6" data-id="element-452" />
                  <span data-id="element-453">Download for Android</span>
                </a>
                <a href="https://apps.apple.com/ch/app/ari-companion/id6747385775" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-3 px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-454">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" data-id="element-455">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" data-id="element-456" />
                  </svg>
                  <span data-id="element-457">Download for iOS</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture Overview Section */}
        <section id="architecture" className="py-32 px-6" data-id="element-458">
          <div className="max-w-7xl mx-auto" data-id="element-459">
            <div className="text-center mb-16" data-id="element-460">
              <div className="inline-block mb-6" data-id="element-461">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 via-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-462">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-463">
                    ARCHITECTURE
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-464">
                Ecosystem Overview
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-465">
                A unified platform connecting devices, apps, and cloud services
              </p>
            </div>

            {/* Architecture Grid */}
            <div className="relative max-w-6xl mx-auto" data-id="element-466">
              {/* Connection Lines Layer - Hidden on mobile */}
              <div className="hidden lg:block absolute inset-0 pointer-events-none" data-id="element-467">
                {/* Companion App to Supported Devices connection (Violet) - Centered vertically */}
                <div className="absolute top-[140px] left-[50%] w-[33.33%] h-1 bg-[#8846DE] shadow-[0_0_15px_rgba(136,70,222,0.5)]" data-id="element-468"></div>

                {/* Ari User Cloud to Business Cloud connection (Red) - Horizontal line between bottom bubbles */}
                <div className="absolute top-[560px] left-[25%] w-[50%] h-1 bg-[#f64f59] shadow-[0_0_15px_rgba(246,79,89,0.5)]" data-id="element-469"></div>

                {/* Orange connecting lines - Web Portal to Supported Devices to both Clouds */}
                {/* Horizontal line connecting Web Portal to Supported Devices (bottom of top row cards) - shortened */}
                <div className="absolute top-[360px] left-[12.5%] w-[75%] h-1 bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-470"></div>

                {/* Vertical line from horizontal orange line down to Ari User Cloud */}
                <div className="absolute top-[360px] left-[22.5%] w-1 h-[200px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-471"></div>

                {/* Vertical line from horizontal orange line down to Business Cloud - centered with Business Cloud bubble */}
                <div className="absolute top-[360px] left-[77.5%] w-1 h-[200px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-472"></div>

                {/* Vertical line coming down from Web Portal - centered with the card */}
                <div className="absolute top-[280px] left-[12.5%] w-1 h-[84px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-473"></div>

                {/* Vertical line coming down from Supported Devices - centered with the card */}
                <div className="absolute top-[280px] left-[87.5%] w-1 h-[84px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-474"></div>
              </div>

              {/* Top Row - 3 Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-32 mb-32 relative z-10" data-id="element-475">
                {/* Web Portal */}
                <button onClick={() => scrollToSection('chat')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(253,160,133,0.4),0_0_0_1px_rgba(253,160,133,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#fda085]/40 hover:shadow-[0_30px_80px_-20px_rgba(253,160,133,0.6),0_0_40px_rgba(253,160,133,0.3)] relative overflow-hidden cursor-pointer" data-id="element-476">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(253,160,133,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-477"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#fda085]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-478"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-479">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#fda085]/20 to-[#fda085]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(253,160,133,0.1)] border border-[#fda085]/20 group-hover:shadow-[0_0_30px_rgba(253,160,133,0.4)] transition-all duration-400" data-id="element-480">
                      <MonitorIcon size={28} className="text-[#fda085] group-hover:scale-110 transition-transform duration-400" data-id="element-481" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-482">
                      Web Portal
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-483">
                      Desktop Interface
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-484">
                      <span data-id="element-485">• Full chat interface</span>
                      <span data-id="element-486">• File management</span>
                      <span data-id="element-487">• Settings control</span>
                      <span data-id="element-488">• Cross-device sync</span>
                    </div>
                  </div>
                </button>

                {/* Companion App */}
                <button onClick={() => scrollToSection('mobile-app')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(196,113,237,0.4),0_0_0_1px_rgba(196,113,237,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#c471ed]/40 hover:shadow-[0_30px_80px_-20px_rgba(196,113,237,0.6),0_0_40px_rgba(196,113,237,0.3)] relative overflow-hidden cursor-pointer" data-id="element-489">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(196,113,237,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-490"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#c471ed]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-491"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-492">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#c471ed]/20 to-[#c471ed]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(196,113,237,0.1)] border border-[#c471ed]/20 group-hover:shadow-[0_0_30px_rgba(196,113,237,0.4)] transition-all duration-400" data-id="element-493">
                      <SmartphoneIcon size={28} className="text-[#c471ed] group-hover:scale-110 transition-transform duration-400" data-id="element-494" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-495">
                      Companion App
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-496">
                      iOS & Android Controller
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-497">
                      <span data-id="element-498">• Device control</span>
                      <span data-id="element-499">• Chat with AI</span>
                      <span data-id="element-500">• Media management</span>
                    </div>
                  </div>
                </button>

                {/* Supported Devices */}
                <button onClick={() => scrollToSection('ari-os')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(233,161,101,0.4),0_0_0_1px_rgba(233,161,101,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#E9A165]/40 hover:shadow-[0_30px_80px_-20px_rgba(233,161,101,0.6),0_0_40px_rgba(233,161,101,0.3)] relative overflow-hidden cursor-pointer" data-id="element-501">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(233,161,101,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-502"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#E9A165]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-503"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-504">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#E9A165]/20 to-[#E9A165]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(233,161,101,0.1)] border border-[#E9A165]/20 group-hover:shadow-[0_0_30px_rgba(233,161,101,0.4)] transition-all duration-400" data-id="element-505">
                      <LayersIcon size={28} className="text-[#E9A165] group-hover:scale-110 transition-transform duration-400" data-id="element-506" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-507">
                      Supported Devices
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-508">
                      Smart Device Ecosystem
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center mt-2" data-id="element-509">
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-510">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-511" />
                        RealWear
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-512">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-513" />
                        Rokid
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-514">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-515" />
                        Vuzix
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-516">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-517" />
                        Others
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Bottom Row - 2 Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-32 relative z-10" data-id="element-518">
                {/* Ari User Cloud */}
                <button onClick={() => scrollToSection('cloud')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(246,79,89,0.4),0_0_0_1px_rgba(246,79,89,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#f64f59]/40 hover:shadow-[0_30px_80px_-20px_rgba(246,79,89,0.6),0_0_40px_rgba(246,79,89,0.3)] relative overflow-hidden cursor-pointer" data-id="element-519">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(246,79,89,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-520"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#f64f59]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-521"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-522">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#f64f59]/20 to-[#f64f59]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(246,79,89,0.1)] border border-[#f64f59]/20 group-hover:shadow-[0_0_30px_rgba(246,79,89,0.4)] transition-all duration-400" data-id="element-523">
                      <CloudIcon size={28} className="text-[#f64f59] group-hover:scale-110 transition-transform duration-400" data-id="element-524" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-525">
                      User Cloud
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-526">
                      Personal Hub
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-527">
                      <span data-id="element-528">• AI memory & context</span>
                      <span data-id="element-529">• Files & documents</span>
                      <span data-id="element-530">• Photos & videos</span>
                      <span data-id="element-531">• Device settings</span>
                      <span data-id="element-532">• Chat history</span>
                    </div>
                  </div>
                </button>

                {/* Business Cloud */}
                <button onClick={() => scrollToSection('business')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(246,79,89,0.4),0_0_0_1px_rgba(246,79,89,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#f64f59]/40 hover:shadow-[0_30px_80px_-20px_rgba(246,79,89,0.6),0_0_40px_rgba(246,79,89,0.3)] relative overflow-hidden cursor-pointer" data-id="element-533">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(246,79,89,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-534"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#f64f59]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-535"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-536">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#f64f59]/20 to-[#f64f59]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(246,79,89,0.1)] border border-[#f64f59]/20 group-hover:shadow-[0_0_30px_rgba(246,79,89,0.4)] transition-all duration-400" data-id="element-537">
                      <BuildingIcon size={28} className="text-[#f64f59] group-hover:scale-110 transition-transform duration-400" data-id="element-538" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-539">
                      Business Cloud
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-540">
                      Enterprise Control Hub
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex flex-col items-center justify-center gap-1" data-id="element-541">
                      <div className="flex items-center justify-center gap-2 flex-wrap" data-id="element-542">
                        <span data-id="element-543">• Fleet management</span>
                        <span data-id="element-544">• Mass deployment</span>
                      </div>
                      <div className="flex items-center justify-center gap-2 flex-wrap" data-id="element-545">
                        <span data-id="element-546">• Analytics</span>
                        <span data-id="element-547">• Security policies</span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Developers Section */}
        <section id="developers" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-id="element-548">
          <div className="max-w-7xl mx-auto" data-id="element-549">
            <div className="text-center mb-16" data-id="element-550">
              <div className="inline-block mb-6" data-id="element-551">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 via-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-552">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-553">
                    DEVELOPERS
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-554">
                Build on Ari OS
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-555">
                Comprehensive APIs, SDKs, and tools to extend the AriOS
                ecosystem
              </p>
            </div>

            {/* Developer Tools Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-556">
              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-557">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-558">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-559">
                    <CodeIcon size={28} className="text-white" data-id="element-560" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-561">
                    REST APIs
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-562">
                    Comprehensive REST APIs for device control, cloud storage,
                    and AI features
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-563">
                    <span data-id="element-564">API Docs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-565" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-566">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-567">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-568">
                    <PackageIcon size={28} className="text-white" data-id="element-569" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-570">SDKs</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-571">
                    Native SDKs for iOS, Android, Web, and AR device development
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-572">
                    <span data-id="element-573">Download SDKs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-574" />
                  </div>
                </div>
              </a>

              <a href="https://github.com/realwear" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-575">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-576">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-577">
                    <GitBranchIcon size={28} className="text-white" data-id="element-578" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-579">GitHub</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-580">
                    Open-source examples, sample apps, and community
                    contributions
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-581">
                    <span data-id="element-582">View Repos</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-583" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-584">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-585">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-586">
                    <BookOpenIcon size={28} className="text-white" data-id="element-587" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-588">
                    Documentation
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-589">
                    Comprehensive guides, tutorials, and best practices
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-590">
                    <span data-id="element-591">Read Docs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-592" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-593">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-594">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-595">
                    <TerminalIcon size={28} className="text-white" data-id="element-596" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-597">
                    CLI Tools
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-598">
                    Command-line tools for development, testing, and deployment
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-599">
                    <span data-id="element-600">Install CLI</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-601" />
                  </div>
                </div>
              </a>

              <a href="https://discord.com/invite/mkG5eVXrkS" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-602">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-603">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-604">
                    <UsersIcon size={28} className="text-white" data-id="element-605" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-606">
                    Community
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-607">
                    Join our developer community, forums, and Discord server
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-608">
                    <span data-id="element-609">Join Community</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-610" />
                  </div>
                </div>
              </a>
            </div>

            {/* Code Example */}
            <div className="relative" data-id="element-611"></div>

            {/* OEM CTA */}
            <div className="mt-20 text-center" data-id="element-612">
              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-613">
                AR/AI Device Manufacturer?
              </h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto text-lg" data-id="element-614">
                Join the Ari OS ecosystem and bring AI-powered voice control to
                your devices
              </p>
              <button onClick={() => setContactModalOpen(true)} className="inline-block px-10 py-4 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg" data-id="element-615">
                Partner With Us
              </button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-32 px-6" data-id="element-616">
          <div className="max-w-5xl mx-auto text-center" data-id="element-617">
            <div className="relative" data-id="element-618">
              <div className="absolute -inset-8 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-3xl opacity-20 blur-3xl" data-id="element-619"></div>
              <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl p-16 border-2 border-white/10" data-id="element-620">
                <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-621">
                  Ready to Experience
                  <br data-id="element-622" />
                  the Future?
                </h2>
                <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto" data-id="element-623">
                  Join thousands of users already working hands-free with AriOS
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-624">
                  <a href="https://app.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-12 py-6 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-xl group relative overflow-hidden" data-id="element-625">
                    <span className="relative z-10" data-id="element-626">Get Started Free</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#D25796] via-[#8846DE] to-[#E9A165] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-627"></div>
                  </a>
                  <button onClick={() => scrollToSection('architecture')} className="px-12 py-6 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-xl" data-id="element-628">
                    Ecosystem Architecture
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-16 px-6" data-id="element-629">
          <div className="max-w-7xl mx-auto" data-id="element-630">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-631">
              <div className="flex items-center space-x-3" data-id="element-632">
                <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-8" data-id="element-633" />
              </div>
              <div className="flex flex-col items-center md:items-end gap-2" data-id="element-634">
                <p className="text-gray-400 text-center md:text-right" data-id="element-635">
                  © 2026 AriOS created by RealWear. All rights reserved.
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-400" data-id="element-636">
                  <a href="https://support.realwear.com/hubfs/Ari/ari_terms_of_use_realwear.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-637">
                    Terms of Use
                  </a>
                  <span data-id="element-638">|</span>
                  <a href="/conditions" className="hover:text-white transition-colors" data-id="element-639">
                    Conditions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Contact Modal */}
      {contactModalOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-640">
          <div className="relative w-full max-w-lg bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border-2 border-white/10 shadow-2xl" data-id="element-641">
            {/* Close button */}
            <button onClick={() => setContactModalOpen(false)} className="absolute top-4 right-4 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-642">
              <XIcon size={24} data-id="element-643" />
            </button>

            <div className="p-10 text-center" data-id="element-644">
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-645">
                <MessageSquareIcon size={32} className="text-white" data-id="element-646" />
              </div>

              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-647">
                Partner With Ari OS
              </h3>

              <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-648">
                Interested in bringing Ari OS to your AR or AI device? We'd love
                to hear from you. Send us an email describing your device and
                use case, and we'll happily set up a meeting to explore the
                partnership.
              </p>

              {/* Email */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6" data-id="element-649">
                <p className="text-zinc-400 text-sm mb-2" data-id="element-650">
                  Send your inquiry to
                </p>
                <a href="mailto:ingrid.maes@realwear.com" className="text-xl font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent hover:opacity-80 transition-opacity" data-id="element-651">
                  ingrid.maes@realwear.com
                </a>
              </div>

              {/* What to include */}
              <div className="text-left" data-id="element-652">
                <p className="text-zinc-400 text-sm mb-3" data-id="element-653">
                  Please include in your email:
                </p>
                <div className="space-y-2" data-id="element-654">
                  <div className="flex items-start space-x-3" data-id="element-655">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-656">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-657" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-658">
                      Your company and role
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-659">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-660">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-661" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-662">
                      A brief description of your device
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-663">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-664">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-665" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-666">
                      How you'd like to integrate with Ari OS
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              
            </div>
          </div>
        </div>}

      <style data-id="element-667">{`
        /* Apple-inspired smooth animations */
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        
        @keyframes grid-flow {
          0% { transform: translateY(0); }
          100% { transform: translateY(100px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(136, 70, 222, 0.3); }
          50% { box-shadow: 0 0 40px rgba(136, 70, 222, 0.6), 0 0 60px rgba(233, 161, 101, 0.4); }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 15s ease infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        
        .animate-grid-flow {
          animation: grid-flow 20s linear infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        
        .animate-glow-pulse {
          animation: glow-pulse 3s ease-in-out infinite;
        }
        
        /* Glass morphism effects */
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px 0 rgba(0, 0, 0, 0.37),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.05);
        }
        
        .glass-card:hover {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 
            0 12px 48px 0 rgba(0, 0, 0, 0.5),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(136, 70, 222, 0.2);
        }
        
        /* Smooth scroll */
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #E9A165, #8846DE, #D25796);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #D25796, #8846DE, #E9A165);
        }
      `}</style>
    </div>;
}